import * as React from 'react'
import moment from 'moment'
import getConfig from 'next/config'
import styled from 'styled-components'
import { Dialog } from '../common/Dialog'
import { Elements, StripeProvider } from 'react-stripe-elements'
import StripeCheckoutForm from '../stripe/StripeCheckoutForm'
import { ReservationState, Modal } from './useReservationState'
import { GlobalContext } from '../../globalContext'
import { I18nCatalogue } from '../../i18n/reservation/types'
import { getPreferredLanguage } from '../../i18n'
import { HairStylist } from '../../types/hairStylist'
import { Menu } from '../../types/menu'
import { createReservation } from '../../types/reservation'
import { numberToCommaSeparatedString } from '../../utils/text'
import { Select } from '../common/Select'
import { TextArea } from '../common/TextArea'

interface Props {
  readonly catalogue: I18nCatalogue
  readonly pageState: ReservationState
  readonly stylist: HairStylist
  readonly menus: Menu[]
  readonly fee: number
  readonly onReserved?: () => void
}

export function Reserve({ catalogue, pageState, stylist, fee, menus, onReserved }: Props) {
  const { me } = React.useContext(GlobalContext)

  if (!me) {
    // NOTE: A user is supposed to be redirected. So, just return `null` here.
    return null
  }

  if (me.user.temporary_user) {
    alert(catalogue.reserv.tempUser)
    return null
  }
  if (!me.user.phoneNumber || me.user.phoneNumber === '未入力') {
    alert(catalogue.reserv.notPhoneNumber)
    return null
  }

  if (pageState.reservedSpot!.specialSpot) {
    menus = menus.filter(menu => menu.special_spot && menu.special_spot.id === pageState.reservedSpot!.specialSpot.id)
  } else {
    menus = menus.filter(menu => !menu.special_spot)
  }
  if (menus.length === 0) {
    alert(catalogue.reserv.notSelectMenu)
    return null
  }

  const { publicRuntimeConfig } = getConfig()

  const stripePublicAPIKey =
    publicRuntimeConfig.stripePublicAPIKey !== ''
      ? publicRuntimeConfig.stripePublicAPIKey
      : process.env.STRIPE_PUBLISHABLE_KEY

  const language = getPreferredLanguage()

  const menuOption = menus.map(menu => {
    const menuName = language == 'ja' ? menu.menu_ja : menu.menu_en
    const label = menu.special_spot ? `【${menu.special_spot.name}】${menuName}` : menuName

    return {
      label: label,
      value: menu.id,
    }
  })

  const spots = pageState.spots

  const timeOption = spots.map(spot => {
    const spotTime = moment(spot.dateTime, 'YYYY/MM/DD HH:mm')
      .format('HH:mm')
      .toString()
    const spotId = spot ? spot.id : ''

    return {
      label: spotTime,
      value: spotId,
    }
  })

  const [state, setState] = React.useState<{ selectedMenuId: string; spotId: string; message: string }>({
    selectedMenuId: menus[0].id,
    spotId: pageState.reservedSpot!.id.toString(),
    message: '',
  })

  function onClose() {
    pageState.setState({
      modal: Modal.None,
      selectedTime: '',
      reservedSpot: null,
      cancelReservation: null,
      spots: [],
      isMobile: false,
    })
  }

  async function callReservation(tokenId: string) {
    /** call Reserve API. */
    const isSuccess = await createReservation(state.spotId, tokenId, state.selectedMenuId, state.message)

    if (!isSuccess) {
      alert(catalogue.reserv.failed)
    } else {
      alert(catalogue.reserv.succeeded)
      if (onReserved) {
        onReserved()
      }
      onClose()
    }
  }

  function getSalonNameById(salonId: string) {
    let salonName = ''
    stylist.salons.map(salon => {
      if (salon.id.toString() == salonId) {
        salonName = salon.name
      }
    })

    return salonName
  }

  function getSalonAddressById(salonId: string) {
    let salonAddress = ''
    stylist.salons.map(salon => {
      if (salon.id.toString() == salonId) {
        salonAddress = salon.address
      }
    })

    return salonAddress
  }

  function getSalonAddressWithPostCodeById(salonId: string) {
    let salonAddress = ''
    stylist.salons.map(salon => {
      if (salon.id.toString() == salonId) {
        salonAddress = '〒' + salon.postCode + ' ' + salon.address
      }
    })

    return salonAddress
  }

  function getMenuPriceByMenuId(menuId: string) {
    let menuPrice = ''
    menus.map(menu => {
      if (menu.id.toString() == menuId) {
        menuPrice = menu.price
      }
    })

    if (menuPrice == '') {
      return
    }

    const result = '¥' + numberToCommaSeparatedString(menuPrice) + ' (' + catalogue.excludingTax + ')'

    return result
  }

  function getTreatmentTimeByMenuId(menuId: string) {
    let treatmentTime = ''
    let spotTime!: moment.Moment

    menus.map(menu => {
      if (menu.id.toString() == menuId) {
        treatmentTime = menu.time
      }
    })

    if (treatmentTime == '') {
      return
    }

    if (pageState.isMobile) {
      pageState.spots!.map(spot => {
        if (spot.id.toString() == state.spotId) {
          spotTime = moment(spot.dateTime, 'YYYY/MM/DD HH:mm')
        }
      })
    } else {
      spotTime = moment(pageState.selectedTime, 'YYYY/MM/DD HH:mm')
    }

    const startTime = spotTime.format('HH:mm')
    const endTime = spotTime.add({ minutes: parseInt(treatmentTime) }).format('HH:mm')
    return startTime + ' - ' + endTime
  }

  function selectMenuChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const menuId = event.target.value
    setState({ selectedMenuId: menuId, spotId: state.spotId, message: state.message })
  }

  function selectSpotIdChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const spotId = event.target.value
    setState({ selectedMenuId: state.selectedMenuId, spotId: spotId, message: state.message })
  }

  return (
    <Dialog
      onClose={onClose}
      title={catalogue.title}
      data-test="Reservation-of-modal"
      actions={[{ label: catalogue.cancel, look: 'white' }]}>
      <DialogItem>
        <span style={{ float: 'left', marginRight: '18px' }}>{catalogue.reservationDateTime}:</span>
        <span>
          {!pageState.isMobile ? (
            pageState.selectedTime
          ) : (
            <Select
              options={timeOption}
              style={{ marginBottom: '12px', width: '100%', fontSize: 14 }}
              defaultValue={state.spotId}
              onChange={selectSpotIdChange}
            />
          )}
        </span>
      </DialogItem>
      <DialogItem>
        <span style={{ marginRight: '18px' }}>{catalogue.salon}:</span>
        <span>{getSalonNameById(pageState.reservedSpot!.salonId)}</span>
      </DialogItem>
      <DialogItem>
        <span style={{ marginRight: '18px' }}>{catalogue.address}:</span>
        <AddressText>{getSalonAddressWithPostCodeById(pageState.reservedSpot!.salonId)}</AddressText>
        <br />
        <Link
          href={`https://maps.google.co.jp/maps?q=${getSalonAddressById(pageState.reservedSpot!.salonId)}`}
          target="_blank">
          {catalogue.map}
        </Link>
      </DialogItem>
      <DialogItem>
        <span style={{ marginRight: '18px' }}>{catalogue.stylist}:</span>
        <span>{stylist.name}</span>
      </DialogItem>
      <DialogItem style={{ height: 64 }}>
        <div style={{ marginBottom: '12px' }}>{catalogue.reserv.selectMenu}</div>
        <Select
          options={menuOption}
          defaultValue={state.selectedMenuId}
          style={{ marginBottom: '12px', width: '100%', fontSize: 14 }}
          onChange={selectMenuChange}
        />
      </DialogItem>
      <DialogItem>
        <span style={{ marginRight: '18px' }}>{catalogue.treatmentTime}:</span>
        <span>{getTreatmentTimeByMenuId(state.selectedMenuId)}</span>
      </DialogItem>
      <DialogItem>
        <span style={{ marginRight: '18px' }}>{catalogue.menuPrice}:</span>
        <span>{getMenuPriceByMenuId(state.selectedMenuId)}</span>
      </DialogItem>
      <DialogItem>
        <span style={{ marginRight: '18px' }}>{catalogue.reserv.reservationFee}:</span>
        <span>¥ {fee}</span>
      </DialogItem>
      <DialogItem>
        <div>{catalogue.remarks}</div>
        <TextArea
          name="message"
          className="input"
          placeholder={catalogue.messageToStylist}
          onChange={e => setState({ ...state, message: e.currentTarget.value })}
          value={state.message}
        />
      </DialogItem>
      <DialogItem>
        <div style={{ marginBottom: '12px' }}>{catalogue.creditSettlement.title}</div>
        <div style={{ fontSize: 13, marginBottom: '12px', fontWeight: 'bold' }}>
          ※{catalogue.reserv.settlementCaution}
        </div>
        <div style={{ fontSize: 13, marginBottom: '24px', fontWeight: 'bold' }}>
          ※{catalogue.reserv.noRefundCaution}
        </div>
        <StripeProvider apiKey={stripePublicAPIKey}>
          <Elements locale={language}>
            <StripeCheckoutForm me={me} catalogue={catalogue} onAction={callReservation} />
          </Elements>
        </StripeProvider>
      </DialogItem>
    </Dialog>
  )
}
const DialogItem = styled.div`
  margin: 18px;
  min-height: 36px;
`

const Link = styled.a`
  display: inline-block;
  margin-top: 5px;
  font-size: 14px;
  color: #999;
`

const AddressText = styled.div`
  display: inline-flex;
  width: 88%;
`
